import { useState } from "react";
import { HeartPulse, X, CheckCircle, AlertCircle, BookOpen, Calendar, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface FloatingPSC1InfoProps {
  position?: "bottom-right" | "bottom-left";
}

const EQUIVALENCES = [
  { code: "PSC1", label: "Prévention et Secours Civiques niveau 1", status: "principal" },
  { code: "SST", label: "Sauveteur Secouriste du Travail", status: "equivalent" },
  { code: "PSE1", label: "Premiers Secours en Équipe niveau 1", status: "equivalent" },
  { code: "PSE2", label: "Premiers Secours en Équipe niveau 2", status: "equivalent" },
  { code: "AFPS", label: "Attestation de Formation aux Premiers Secours", status: "equivalent" },
  { code: "BNPS", label: "Brevet National de Premiers Secours", status: "equivalent" },
  { code: "BNS", label: "Brevet National de Secourisme", status: "equivalent" },
];

export const PSC1Card = () => (
  <Card className="w-full shadow-2xl border-red-500/20 bg-gradient-to-br from-background to-background/95">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg shadow-red-500/20">
          <HeartPulse className="h-5 w-5 text-white" />
        </div>
        <div>
          <CardTitle className="text-base text-red-400">PSC1 — Prérequis BPJEPS</CardTitle>
          <p className="text-xs text-muted-foreground">Premiers Secours obligatoires</p>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4 pt-0">
      <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed">
            Le <strong>PSC1</strong> (ou équivalent) est <strong>obligatoire</strong> pour devenir
            moniteur de jet-ski. Sans ce diplôme, impossible de valider le BPJEPS Activités Nautiques
            mention motonautisme.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
        <Calendar className="h-4 w-4 text-amber-400 shrink-0" />
        <p className="text-xs">
          <strong>Validité :</strong> Le PSC1 doit être <strong>en cours de validité</strong> au moment de l'inscription aux TEP.
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-semibold">Objectif</h4>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed pl-6">
          Cette formation de base aux premiers secours est cruciale car le moniteur est
          <strong> responsable de la sécurité des élèves</strong> et doit être capable d'intervenir
          rapidement en cas d'incident ou d'accident sur l'eau.
        </p>
      </div>
      <div className="space-y-2">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-emerald-400" />
          Équivalences acceptées
        </h4>
        <div className="space-y-1.5">
          {EQUIVALENCES.map((eq) => (
            <div
              key={eq.code}
              className={`flex items-center gap-2 p-1.5 rounded-md text-xs ${
                eq.status === "principal"
                  ? "bg-red-500/10 border border-red-500/20"
                  : "bg-secondary/30"
              }`}
            >
              <Badge
                variant={eq.status === "principal" ? "default" : "outline"}
                className={`text-[10px] h-5 ${
                  eq.status === "principal" ? "bg-red-500 hover:bg-red-500" : ""
                }`}
              >
                {eq.code}
              </Badge>
              <span className="text-muted-foreground">{eq.label}</span>
              {eq.status === "principal" && (
                <Badge variant="secondary" className="text-[10px] h-5 ml-auto bg-emerald-500/20 text-emerald-400">
                  Requis
                </Badge>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
        <div className="flex items-start gap-2">
          <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-primary">Accès à la formation BPJEPS</p>
            <p className="text-xs text-muted-foreground mt-1">
              La détention du PSC1 (ou équivalent) est l'une des <strong>premières conditions
              administratives</strong> exigées pour s'inscrire aux tests d'entrée en formation BPJEPS.
            </p>
          </div>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground text-center pt-1 border-t border-border/30">
        Source : Arrêté 22 juil. 2019 modifié — Ministère des Sports
      </p>
    </CardContent>
  </Card>
);

export const FloatingPSC1Info = ({ position = "bottom-right" }: FloatingPSC1InfoProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const posClasses = position === "bottom-right"
    ? "right-4 bottom-4"
    : "left-4 bottom-4";

  return (
    <div className={`fixed ${posClasses} z-40`}>
      {/* Floating Trigger Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:scale-110 transition-all duration-300"
          aria-label="Informations PSC1"
        >
          <HeartPulse className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-background">
            1
          </span>
          {/* Tooltip */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-background/95 backdrop-blur-sm border border-red-500/30 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl">
            <p className="text-sm font-medium text-red-400">PSC1 obligatoire</p>
            <p className="text-xs text-muted-foreground">Clique pour les détails</p>
          </div>
        </button>
      )}

      {/* Expanded Card */}
      {isOpen && (
        <div className="animate-in fade-in zoom-in-95 duration-200 relative w-[340px] sm:w-[400px]">
          <button
            onClick={() => setIsOpen(false)}
            className="absolute right-3 top-3 z-10 p-1.5 rounded-full hover:bg-secondary/50 transition-colors"
            aria-label="Fermer"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
          <PSC1Card />
        </div>
      )}
    </div>
  );
};
